import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { authAPI, playerAPI, tournamentAPI, matchAPI, commentAPI, notificationAPI, setToken } from '../services/api';

const AppContext = createContext();
export const useApp = () => useContext(AppContext);

const TOKEN_KEY = 'tennisbuzz_token';

export function AppProvider({ children }) {
  const [players, setPlayers] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [matches, setMatches] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Bootstrap: only restore auth ──
  useEffect(() => {
    const init = async () => {
      try {
        const token = localStorage.getItem(TOKEN_KEY);
        if (token) {
          try {
            const { user } = await authAPI.me();
            setCurrentUser(user);
          } catch {
            localStorage.removeItem(TOKEN_KEY);
          }
        }
      } catch (e) {
        console.error('Init error:', e);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  const fetchPlayers = useCallback(async () => {
    try {
      const res = await playerAPI.getAll('limit=10000&sort=ranking');
      setPlayers(res.data || []);
      return res.data || [];
    } catch (e) { console.error(e); return []; }
  }, []);

  const fetchTournaments = useCallback(async (query = 'limit=100') => {
    try {
      const res = await tournamentAPI.getAll(query);
      setTournaments(res.data || []);
      return res.data || [];
    } catch (e) { console.error(e); return []; }
  }, []);

  const fetchMatches = useCallback(async (query = 'limit=500') => {
    try {
      const res = await matchAPI.getAll(query);
      setMatches(res.data || []);
      return res.data || [];
    } catch (e) { console.error(e); return []; }
  }, []);

  const refreshData = useCallback(async () => {
    await Promise.all([fetchPlayers(), fetchTournaments(), fetchMatches()]);
  }, [fetchPlayers, fetchTournaments, fetchMatches]);

  // ── Auth ──
  const login = useCallback(async (username, password) => {
    try {
      const { token, user } = await authAPI.login({ username, password });
      setToken(token);
      setCurrentUser(user);
      return { success: true, user };
    } catch (err) { 
      return { success: false, message: err.response?.data?.message || 'Invalid credentials' };
    }
  }, []);

  const logout = useCallback(async () => {
    // Remove push subscription from this user
    try {
      if ('serviceWorker' in navigator && 'PushManager' in window) {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        if (sub) await notificationAPI.unsubscribe(sub.endpoint).catch(() => {});
      }
    } catch {}
    setToken(null);
    setCurrentUser(null);
  }, []);

  const register = useCallback(async (username, password, name, role, country, orgDetails, playerDetails) => {
    try {
      const body = { username, password, name, role, country };
      if (role === 'organizer' && orgDetails) Object.assign(body, orgDetails);
      if (role === 'player' && playerDetails) {
        body.playerPhone = playerDetails.phone;
        body.playerEmail = playerDetails.email;
        body.playerAge = playerDetails.age;
      }
      const { token, user } = await authAPI.register(body);
      setToken(token);
      setCurrentUser(user);
      return { success: true, user };
    } catch (err) { 
      return { success: false, message: err.response?.data?.message || 'Username already taken' };
    }
  }, []);

  // ── Lookups ──
  const getPlayer = useCallback(id => players.find(p => p.id === id || p._id === id), [players]);
  const getTournament = useCallback(id => tournaments.find(t => t.id === id || t._id === id), [tournaments]);
  const getMatch = useCallback(id => matches.find(m => m.id === id || m._id === id), [matches]);
  const getMatchesForTournament = useCallback(tId => matches.filter(m => m.tournamentId === tId), [matches]);
  const getMatchesForPlayer = useCallback(pId => matches.filter(m => {
    const check = val => val && (val === pId || (val.includes && val.includes('+') && val.split('+').includes(pId)));
    return check(m.player1) || check(m.player2);
  }), [matches]);

  // ── Tournament CRUD ──
  const addTournament = useCallback(async (data) => {
    try {
      const { data: t } = await tournamentAPI.create(data);
      setTournaments(prev => [...prev, t]);
      return t;
    } catch (e) { console.error(e); return null; }
  }, []);

  const updateTournament = useCallback(async (id, data) => {
    try {
      const { data: t } = await tournamentAPI.update(id, data);
      setTournaments(prev => prev.map(x => (x.id === id || x._id === id) ? t : x));
    } catch (e) { console.error(e); }
  }, []);

  const deleteTournament = useCallback(async (id) => {
    try {
      await tournamentAPI.remove(id);
      setTournaments(prev => prev.filter(t => t.id !== id && t._id !== id));
      setMatches(prev => prev.filter(m => m.tournamentId !== id));
    } catch (e) { console.error(e); }
  }, []);

  // ── Join / Leave ──
  const joinTournament = useCallback(async (tournamentId, playerId, partnerId) => {
    try {
      const { data: t } = await tournamentAPI.join(tournamentId, { playerId, partnerId });
      setTournaments(prev => prev.map(x => (x.id === tournamentId || x._id === tournamentId) ? t : x));
    } catch (e) { console.error(e); }
  }, []);

  const leaveTournament = useCallback(async (tournamentId, playerId) => {
    try {
      const { data: t } = await tournamentAPI.leave(tournamentId, { playerId });
      setTournaments(prev => prev.map(x => (x.id === tournamentId || x._id === tournamentId) ? t : x));
    } catch (e) { console.error(e); }
  }, []);

  // ── Bracket ──
  const setTournamentSeeds = useCallback(async (tournamentId, seeds) => {
    try {
      const { data: t } = await tournamentAPI.setSeeds(tournamentId, seeds);
      setTournaments(prev => prev.map(x => (x.id === tournamentId || x._id === tournamentId) ? t : x));
    } catch (e) { console.error(e); }
  }, []);

  const resetTournament = useCallback(async (tournamentId, flags) => {
    try {
      const { data: t } = await tournamentAPI.reset(tournamentId, flags);
      setTournaments(prev => prev.map(x => (x.id === tournamentId || x._id === tournamentId) ? t : x));
      if (flags.removeMatches !== false) {
        setMatches(prev => prev.filter(m => m.tournamentId !== tournamentId));
      }
      return t;
    } catch (e) { console.error(e); return null; }
  }, []);

  const completeTournament = useCallback(async (tournamentId) => {
    try {
      const res = await tournamentAPI.completeTournament(tournamentId);
      setTournaments(prev => prev.map(x => (x.id === tournamentId || x._id === tournamentId) ? res.data : x));
      return res;
    } catch (e) { console.error(e); return null; }
  }, []);

  const generateKnockoutBracket = useCallback(async (tournamentId) => {
    try {
      const res = await tournamentAPI.generateBracket(tournamentId);
      setTournaments(prev => prev.map(x => (x.id === tournamentId || x._id === tournamentId) ? res.data : x));
      const { data: newMatches } = await matchAPI.getByTournament(tournamentId);
      setMatches(prev => [...prev.filter(m => m.tournamentId !== tournamentId), ...newMatches]);
    } catch (e) { console.error(e); }
  }, []);

  const generateAIDraw = useCallback(async (tournamentId) => {
    try {
      const res = await tournamentAPI.generateAIDraw(tournamentId);
      setTournaments(prev => prev.map(x => (x.id === tournamentId || x._id === tournamentId) ? res.data : x));
      const { data: newMatches } = await matchAPI.getByTournament(tournamentId);
      setMatches(prev => [...prev.filter(m => m.tournamentId !== tournamentId), ...newMatches]);
      return res;
    } catch (e) { console.error(e); throw e; }
  }, []);

  const generateRoundRobinFixtures = useCallback(async (tournamentId) => {
    try {
      const res = await tournamentAPI.generateBracket(tournamentId);
      setTournaments(prev => prev.map(x => (x.id === tournamentId || x._id === tournamentId) ? res.data : x));
      const { data: newMatches } = await matchAPI.getByTournament(tournamentId);
      setMatches(prev => [...prev.filter(m => m.tournamentId !== tournamentId), ...newMatches]);
    } catch (e) { console.error(e); }
  }, []);

  // ── Score ──
  const updateScore = useCallback(async (matchId, score, status, winner) => {
    try {
      const { data: m } = await matchAPI.updateScore(matchId, { score, status, winner });
      setMatches(prev => prev.map(x => (x.id === matchId || x._id === matchId) ? m : x));
      if (winner) {
        // Only re-fetch tournament matches to get advanced bracket slots
        const { data: updated } = await matchAPI.getByTournament(m.tournamentId);
        setMatches(prev => [...prev.filter(x => x.tournamentId !== m.tournamentId), ...updated]);
      }
    } catch (e) { console.error(e); }
  }, []);

  const updateMatch = useCallback(async (matchId, data) => {
    try {
      const { data: m } = await matchAPI.updateScore(matchId, data);
      setMatches(prev => prev.map(x => (x.id === matchId || x._id === matchId) ? m : x));
    } catch (e) { console.error(e); }
  }, []);

  const addMatch = useCallback(async (data) => {
    try {
      const { data: m } = await matchAPI.create(data);
      setMatches(prev => [...prev, m]);
      return m;
    } catch (e) { console.error(e); return null; }
  }, []);

  const resolveMatchSpecial = useCallback(async (matchId, winnerId, reason) => {
    try {
      const { data: m } = await matchAPI.resolveSpecial(matchId, { winner: winnerId, reason });
      setMatches(prev => prev.map(x => (x.id === matchId || x._id === matchId) ? m : x));
      // Re-fetch tournament matches to get advanced bracket slots
      const { data: updated } = await matchAPI.getByTournament(m.tournamentId);
      setMatches(prev => [...prev.filter(x => x.tournamentId !== m.tournamentId), ...updated]);
    } catch (e) { console.error(e); }
  }, []);

  // ── Standings ──
  const getRoundRobinStandings = useCallback(async (tournamentId) => {
    try {
      const { data } = await tournamentAPI.getStandings(tournamentId);
      return data;
    } catch { return []; }
  }, []);

  // ── Player ──
  const addPlayer = useCallback(async (data) => {
    try {
      const { data: p } = await playerAPI.create(data);
      setPlayers(prev => [...prev, p]);
      return p;
    } catch (e) { console.error(e); return null; }
  }, []);

  const bulkAddPlayers = useCallback(async (playerList) => {
    try {
      const { data } = await playerAPI.bulk(playerList);
      if (data.created.length > 0) setPlayers(prev => [...prev, ...data.created]);
      return data;
    } catch (e) { console.error(e); return null; }
  }, []);

  const bulkAddPlayersToTournament = useCallback(async (tournamentId, playerEntries) => {
    try {
      const { data, tournament, players: returnedPlayers } = await tournamentAPI.bulkAddPlayers(tournamentId, playerEntries);
      
      // Update tournament immediately with the response
      if (tournament) {
        setTournaments(prev => prev.map(x => (x.id === tournamentId || x._id === tournamentId) ? tournament : x));
      }
      
      // Merge returned players into state (both new and existing)
      if (returnedPlayers && returnedPlayers.length > 0) {
        setPlayers(prev => {
          const existingIds = new Set(prev.map(p => String(p.id || p._id)));
          const newPlayers = returnedPlayers.filter(p => !existingIds.has(String(p.id || p._id)));
          return [...prev, ...newPlayers];
        });
      }
      
      return data;
    } catch (e) { console.error(e); return null; }
  }, []);

  const updatePlayer = useCallback(async (id, data) => {
    try {
      const { data: p } = await playerAPI.update(id, data);
      setPlayers(prev => prev.map(x => (x.id === id || x._id === id) ? p : x));
    } catch (e) { console.error(e); }
  }, []);

  // ── Coupons ──
  const addCoupon = useCallback(async (tournamentId, coupon) => {
    try {
      await tournamentAPI.addCoupon(tournamentId, coupon);
      const { data: t } = await tournamentAPI.getOne(tournamentId);
      setTournaments(prev => prev.map(x => (x.id === tournamentId || x._id === tournamentId) ? t : x));
    } catch (e) { console.error(e); }
  }, []);

  const deleteCoupon = useCallback(async (tournamentId, couponId) => {
    try {
      await tournamentAPI.deleteCoupon(tournamentId, couponId);
      const { data: t } = await tournamentAPI.getOne(tournamentId);
      setTournaments(prev => prev.map(x => (x.id === tournamentId || x._id === tournamentId) ? t : x));
    } catch (e) { console.error(e); }
  }, []);

  const applyCoupon = useCallback(async (tournamentId, code, entryFee) => {
    try {
      const res = await tournamentAPI.applyCoupon(tournamentId, { code, entryFee });
      return { valid: true, discount: res.discount, finalAmount: res.finalAmount, couponId: res.couponId, msg: `₹${res.discount} off applied!` };
    } catch (e) { return { valid: false, msg: e.message }; }
  }, []);

  const useCoupon = useCallback(async (tournamentId, couponId) => {
    try { await tournamentAPI.useCoupon(tournamentId, couponId); } catch (e) { console.error(e); }
  }, []);

  // ── Check-in ──
  const checkInPlayer = useCallback(async (tournamentId, playerId) => {
    try {
      const { data: t } = await tournamentAPI.checkIn(tournamentId, { playerId });
      setTournaments(prev => prev.map(x => (x.id === tournamentId || x._id === tournamentId) ? t : x));
    } catch (e) { console.error(e); }
  }, []);

  const checkOutPlayer = useCallback(async (tournamentId, playerId) => {
    try {
      const { data: t } = await tournamentAPI.checkOut(tournamentId, { playerId });
      setTournaments(prev => prev.map(x => (x.id === tournamentId || x._id === tournamentId) ? t : x));
    } catch (e) { console.error(e); }
  }, []);

  // ── Announcements ──
  const addAnnouncement = useCallback(async (tournamentId, title, message) => {
    try {
      await tournamentAPI.addAnnouncement(tournamentId, { title, message });
      const { data: t } = await tournamentAPI.getOne(tournamentId);
      setTournaments(prev => prev.map(x => (x.id === tournamentId || x._id === tournamentId) ? t : x));
    } catch (e) { console.error(e); }
  }, []);

  const deleteAnnouncement = useCallback(async (tournamentId, annId) => {
    try {
      await tournamentAPI.deleteAnnouncement(tournamentId, annId);
      const { data: t } = await tournamentAPI.getOne(tournamentId);
      setTournaments(prev => prev.map(x => (x.id === tournamentId || x._id === tournamentId) ? t : x));
    } catch (e) { console.error(e); }
  }, []);

  // ── Discussion ──
  const addDiscussionMessage = useCallback(async (tournamentId, text) => {
    try {
      await tournamentAPI.addDiscussion(tournamentId, { text });
      const { data: t } = await tournamentAPI.getOne(tournamentId);
      setTournaments(prev => prev.map(x => (x.id === tournamentId || x._id === tournamentId) ? t : x));
    } catch (e) { console.error(e); }
  }, []);

  // ── Comments ──
  const addComment = useCallback(async (matchId, text) => {
    try {
      const { data: c } = await commentAPI.create(matchId, { text });
      return c;
    } catch { return null; }
  }, []);

  const getCommentsForMatch = useCallback(async (matchId) => {
    try {
      const { data } = await commentAPI.getByMatch(matchId);
      return data;
    } catch { return []; }
  }, []);

  // ── Notifications ──
  const addNotification = useCallback(async (tournamentId, message, targetPlayerIds) => {
    try {
      const { data: n } = await notificationAPI.create(tournamentId, { message, targetPlayerIds });
      return n;
    } catch { return null; }
  }, []);

  const getNotificationsForPlayer = useCallback(async (playerId) => {
    try {
      const { data } = await notificationAPI.getForPlayer(playerId);
      return data;
    } catch { return []; }
  }, []);

  const markNotificationRead = useCallback(async (notifId, userId) => {
    try { await notificationAPI.markRead(notifId, { userId }); } catch (e) { console.error(e); }
  }, []);

  const users = [];

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg, #F8F9FC)' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>🎾</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#00205B', fontFamily: "'Outfit', sans-serif", letterSpacing: 2 }}>TENNISBUZZ</div>
          <div style={{ fontSize: 12, color: '#8892A6', marginTop: 4 }}>Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <AppContext.Provider value={{
      players, tournaments, matches, users, currentUser, loading,
      isAdvanced: currentUser?.plan === 'advanced',
      fetchPlayers, fetchTournaments, fetchMatches, refreshData,
      login, logout, register, getPlayer, getTournament, getMatch,
      addTournament, updateTournament, deleteTournament,
      joinTournament, leaveTournament,
      generateKnockoutBracket, generateRoundRobinFixtures, generateAIDraw, resetTournament,
      setTournamentSeeds,
      addMatch, updateMatch, updateScore, addPlayer, bulkAddPlayers, bulkAddPlayersToTournament, updatePlayer,
      getMatchesForTournament, getMatchesForPlayer, getRoundRobinStandings,
      completeTournament,
      addCoupon, deleteCoupon, applyCoupon, useCoupon,
      addComment, getCommentsForMatch,
      checkInPlayer, checkOutPlayer,
      addNotification, getNotificationsForPlayer, markNotificationRead,
      resolveMatchSpecial,
      addAnnouncement, deleteAnnouncement,
      addDiscussionMessage,
    }}>
      {children}
    </AppContext.Provider>
  );
}
